import { useState, useEffect } from "react";
import { displayAmount } from "../lib/display-amount";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { AccountRow } from "../lib/accountRows";
import type { Transaction } from "../shared/types";
import { usePolledBalance } from "../lib/use-polled-balance";
import { formatUsd, getUsdValue } from "../lib/prices";
import { isTokenVisible, findNewTokens } from "../lib/displayPrefs";
import { explorerLink } from "../shared/utils";
import { useHideBalances } from "../context/HideBalancesContext";
import { MASK } from "../lib/hidden-amount";
import { CopyableText } from "./ui";
import { fetchTransactions } from "../lib/transactions";
import { getStaleCache, setCache, txCacheKey } from "../lib/dataCache";
import { CompactTxPreview } from "./CompactTxPreview";
import { truncateBalance } from "./sendTypes";
import { AssetIcon } from "./AssetIcon";
import { orderTokens } from "../lib/asset-order";
import { withNativeKinds } from "../lib/tx-classify";
import { TestnetBadge } from "./testnet-badge";

const DEFAULT_POLL_INTERVAL = 60_000;

/** CSS class for balance change flash animation */
function changeClass(dir: "up" | "down" | null | undefined): string {
  if (dir === "up") return "animate-flash-green";
  if (dir === "down") return "animate-flash-red";
  return "";
}

export function AccountRowView({
  row,
  displayPrefs,
  pollInterval = DEFAULT_POLL_INTERVAL,
  prices,
  onTokenDecision,
  onBalanceUpdate,
}: {
  row: AccountRow;
  displayPrefs: Record<string, boolean> | null;
  pollInterval?: number;
  prices: Record<string, number>;
  onTokenDecision?: (assetId: string, visible: boolean) => void;
  onBalanceUpdate?: (rowKey: string, usdTotal: number) => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hidden } = useHideBalances();
  const [dismissedTokens, setDismissedTokens] = useState<Set<string>>(new Set());

  // Recent transactions preview (fetch once, cached)
  const [recentTxs, setRecentTxs] = useState<Transaction[]>([]);
  const nativeAssetForTx = row.assets.find((a) => a.isNative);
  // Approvals, revokes and contract calls get their own titles (tx-classify), not a 0 ETH send.
  const kinded = (txs: Transaction[]) =>
    row.chain.type === "evm" ? withNativeKinds(txs, { address: row.address, assets: row.assets }) : txs;
  useEffect(() => {
    if (!nativeAssetForTx) return;
    const cacheK = txCacheKey(row.address, row.chain.id);
    const cached = getStaleCache<Transaction[]>(cacheK);
    if (cached) {
      setRecentTxs(kinded(cached.data).slice(0, 3));
      if (cached.fresh) return; // Skip fetch if cache is still fresh
    }
    // Fetch in background (non-blocking if we have stale data)
    fetchTransactions(row.address, row.chain, nativeAssetForTx, 1)
      .then(({ transactions }) => {
        setCache(cacheK, transactions);
        setRecentTxs(kinded(transactions).slice(0, 3));
      })
      .catch(() => {}); // silently hide on error
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.address, row.chain.id]);

  const {
    nativeBalance,
    nativeState,
    tokenBalances,
    tokenState,
    nativeChanged,
    tokenChanges,
    refresh: refreshAll,
  } = usePolledBalance(row, pollInterval);

  const nativeAsset = row.assets.find((a) => a.isNative);
  const nativeSymbol = nativeBalance?.asset.symbol || nativeAsset?.symbol || "";

  function handleNativeClick() {
    if (nativeState !== "loaded" || !nativeAsset) return;
    const suffix = row.btcAddrType ? `/${row.btcAddrType}` : "";
    navigate(`/accounts/${row.keyId}/${row.chain.name.toLowerCase()}/${nativeAsset.symbol}${suffix}`);
  }

  const shortAddr =
    row.address.length > 20
      ? `${row.address.slice(0, 8)}...${row.address.slice(-6)}`
      : row.address;

  const chainColor =
    row.addressType === "evm" ? "bg-blue-500" : row.addressType === "solana" ? "bg-purple-500" : "bg-orange-500";

  const nativeUsd = nativeBalance
    ? getUsdValue(nativeBalance.formatted, nativeSymbol, prices)
    : null;

  // Report aggregated USD value to parent for portfolio total
  useEffect(() => {
    if (!onBalanceUpdate) return;
    const tokenUsdSum = tokenBalances
      .filter((b) => isTokenVisible(b.asset.id, displayPrefs, b.formatted))
      .reduce((sum, b) => sum + (getUsdValue(b.formatted, b.asset.symbol, prices) ?? 0), 0);
    const total = (nativeUsd ?? 0) + tokenUsdSum;
    onBalanceUpdate(`${row.keyId}:${row.chain.id}:${row.address}`, total);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeUsd, tokenBalances, prices]);

  return (
    <div>
      {/* Main row */}
      <div
        data-tour="chain-row"
        className="flex items-center h-[68px] px-3 md:px-5 hover:bg-surface-tertiary/40 transition-colors group cursor-pointer"
        onClick={handleNativeClick}
      >
        {/* Left: icon + label + address */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {row.chain.iconUrl ? (
            <img
              src={row.chain.iconUrl}
              alt={row.label}
              className="w-9 h-9 rounded-full bg-surface-tertiary shrink-0"
            />
          ) : (
            <div
              className={`w-9 h-9 rounded-full ${chainColor} flex items-center justify-center text-xs font-bold text-white shrink-0`}
            >
              {row.addressType === "evm" ? "E" : row.addressType === "solana" ? "S" : "B"}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-text-primary leading-tight flex items-center gap-1.5">
              {row.label.replace(/\s*\(?\s*(testnet|devnet)\s*\)?\s*/gi, " ").trim()}
              <TestnetBadge chainName={row.chain.name} />
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <CopyableText value={row.address} className="text-[11px] text-text-muted" textClassName="font-mono">
                {shortAddr}
              </CopyableText>
              <a
                href={explorerLink(row.chain.explorerUrl, `/address/${row.address}`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-text-muted/40 hover:text-text-tertiary transition-colors"
                title={t("common.viewOnExplorer")}
              >
                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Right: balance + chevron */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right min-w-[4rem]">
            {nativeState === "loading" && (
              <div className="space-y-1.5">
                <div className="h-4 w-16 bg-surface-tertiary rounded animate-pulse" />
                <div className="h-3 w-10 bg-surface-tertiary/60 rounded animate-pulse ml-auto" />
              </div>
            )}
            {nativeState === "error" && (
              <button
                onClick={(e) => refreshAll(e)}
                className="text-xs text-red-500 hover:text-red-400"
              >
                {t("account.failedRetry")}
              </button>
            )}
            {nativeState === "loaded" && (
              <div>
                <div className={`flex items-baseline justify-end gap-1 text-sm tabular-nums font-medium text-text-primary transition-colors duration-700 ${changeClass(nativeChanged)}`}>
                  <span>{hidden ? MASK : (nativeBalance ? displayAmount(truncateBalance(nativeBalance.formatted)) : "0")}</span>
                  <span className="text-[11px] text-text-muted font-normal">{nativeSymbol}</span>
                </div>
                {nativeUsd != null && (
                  <div className={`text-[11px] text-text-muted tabular-nums transition-colors duration-700 ${changeClass(nativeChanged)}`}>{hidden ? MASK : formatUsd(nativeUsd)}</div>
                )}
              </div>
            )}
          </div>

          {/* Chevron */}
          <svg className="w-4 h-4 text-text-muted/30 group-hover:text-text-muted transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>

      {/* Token rows */}
      {tokenState === "loaded" && tokenBalances.length > 0 &&
        // Tokens by USD value, empty ones last (asset-order).
        orderTokens(
          tokenBalances
            .filter((b) => isTokenVisible(b.asset.id, displayPrefs, b.formatted))
            .map((b) => ({ ...b, usd: getUsdValue(b.formatted, b.asset.symbol, prices) })),
        )
          .map((b) => {
            const tokenUsd = b.usd;
            const tokenDir = tokenChanges.get(b.asset.id);
            return (
              <div
                key={b.asset.id}
                className="flex items-center h-14 pl-[60px] md:pl-[68px] pr-3 md:pr-5 border-t border-border-secondary/50 hover:bg-surface-tertiary/40 transition-colors cursor-pointer group"
                onClick={() =>
                  navigate(`/accounts/${row.keyId}/${row.chain.name.toLowerCase()}/${b.asset.symbol}${row.btcAddrType ? `/${row.btcAddrType}` : ""}`)
                }
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  {/* Token logo with its chain as a small badge */}
                  <AssetIcon asset={b.asset} chain={row.chain} size="sm" />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-text-secondary leading-tight flex items-center gap-1.5">
                      {b.asset.name.replace(/\s*\(?\s*(testnet|devnet)\s*\)?\s*/gi, " ").trim()}
                      <TestnetBadge chainName={row.chain.name} small />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right min-w-[4rem]">
                    <div className={`flex items-baseline justify-end gap-1 text-xs tabular-nums font-medium text-text-secondary transition-colors duration-700 ${changeClass(tokenDir)}`}>
                      <span>{hidden ? MASK : displayAmount(truncateBalance(b.formatted))}</span>
                      <span className="text-text-muted font-normal">{b.asset.symbol}</span>
                    </div>
                    {tokenUsd != null && (
                      <div className={`text-[10px] text-text-muted tabular-nums transition-colors duration-700 ${changeClass(tokenDir)}`}>{hidden ? MASK : formatUsd(tokenUsd)}</div>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-text-muted/30 group-hover:text-text-muted transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            );
          })
      }

      {/* Recent transactions preview — only show txs from the last hour */}
      {(() => {
        const cutoff = Math.floor(Date.now() / 1000) - 3600;
        const fresh = recentTxs.filter((tx) => tx.timestamp > cutoff);
        if (fresh.length === 0) return null;
        return (
          <div className="border-t border-border-secondary/50">
            {fresh.map((tx, i) => (
              <CompactTxPreview key={`${tx.hash}-${i}`} tx={tx} explorerUrl={row.chain.explorerUrl} networkName={row.chain.displayName} />
            ))}
          </div>
        );
      })()}

      {/* Token discovery prompts */}
      {tokenState === "loaded" && onTokenDecision && (() => {
        const newTokens = findNewTokens(tokenBalances, displayPrefs)
          .filter((t) => !dismissedTokens.has(t.id));
        if (newTokens.length === 0) return null;
        return newTokens.map((token) => (
          <div
            key={token.id}
            className="px-3 md:px-5 py-2.5 border-t border-border-secondary"
          >
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-text-muted">
                {t("account.tokenFound", { symbol: token.symbol })}
                <span className="tabular-nums ml-1">({hidden ? MASK : token.balance})</span>
              </p>
              <div className="flex items-center gap-3 shrink-0 mr-8">
                <button
                  onClick={() => onTokenDecision(token.id, false)}
                  className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                  {t("account.hide")}
                </button>
                <button
                  onClick={() => setDismissedTokens((prev) => new Set(prev).add(token.id))}
                  className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t("account.later")}
                </button>
                <button
                  onClick={() => onTokenDecision(token.id, true)}
                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t("account.show")}
                </button>
              </div>
            </div>
          </div>
        ));
      })()}

    </div>
  );
}
